// Date-only strings (YYYY-MM-DD) must never be passed to `new Date(d)` alone —
// JS parses those as UTC midnight, which in US timezones is the previous evening
// and makes same-day kiosk/staff due dates look ~4–19 hours overdue.

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export const isDateOnly = (d: string): boolean => DATE_ONLY.test(d.slice(0, 10)) && d.length === 10;

/** Noon local — safe for calendar display without UTC off-by-one. */
const parseLocalDate = (d: string) => new Date(d + 'T12:00:00');

/**
 * Instant a due value becomes overdue.
 * - Date-only → end of that local calendar day (23:59:59.999)
 * - Full timestamp → as stored
 */
export const dueInstant = (dueDate: string): Date => {
  if (isDateOnly(dueDate)) return new Date(dueDate + 'T23:59:59.999');
  return new Date(dueDate);
};

export const formatDate = (d?: string | number | Date | null): string => {
  if (!d) return '—';
  const date = typeof d === 'string' && isDateOnly(d)
    ? parseLocalDate(d)
    : new Date(d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Persist a due value as an ISO instant. Date-only → end of that local day. */
export function dueDateForStorage(due: string): string {
  if (isDateOnly(due)) return new Date(`${due}T23:59:59.999`).toISOString();
  return new Date(due).toISOString();
}

/** Clock for kiosk-style dues — always a time, never a calendar date. */
export function formatDueClock(dueDate?: string | null): string {
  if (!dueDate) return '—';
  const d = dueInstant(dueDate);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
  if (sameDay) return time;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
}

export const fmt12 = (d?: string | null): string => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

export const isPastDue = (dueDate: string): boolean =>
  dueInstant(dueDate).getTime() < Date.now();

export const todayLocalISO = (): string => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

/** overdue / due-today / later — for TV dashboard + queues. */
export function dueBucket(dueDate: string | null | undefined): 'overdue' | 'today' | 'later' {
  if (!dueDate) return 'later';
  if (isPastDue(dueDate)) return 'overdue';
  if (isDateOnly(dueDate)) {
    return dueDate === todayLocalISO() ? 'today' : 'later';
  }
  const due = dueInstant(dueDate);
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  return due <= endOfToday ? 'today' : 'later';
}

/** Human overdue span, e.g. "2 days" or "3 hrs". */
export function overdueByLabel(dueDate: string): string {
  const ms = Date.now() - dueInstant(dueDate).getTime();
  if (ms < 0) return '0 hrs';
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days} day${days !== 1 ? 's' : ''}`;
  const hrs = Math.max(1, Math.floor(ms / 3_600_000));
  return `${hrs} hr${hrs !== 1 ? 's' : ''}`;
}
